import { Loading } from "../app/Status";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router";
import { formatEuro, ORG_STATUS_LABELS, type OrgSubscriptionResponse } from "@klantpunt/shared";
import { Tabs } from "../app/Choice";
import { PageHeader } from "../app/PageHeader";
import { DocTypesSection } from "../features/dossier/DocTypesSection";
import { ClientsSection } from "../features/org/ClientsSection";
import { ReportStyleSection } from "../features/org/ReportStyleSection";
import { api } from "../lib/api";
import { can, hasModule, useMe } from "../lib/auth";
import { UsersSection } from "./Users";

type Tab = "gebruikers" | "klanten" | "documentsoorten" | "rapportstijl" | "abonnement";
const TABS: Tab[] = ["gebruikers", "klanten", "documentsoorten", "rapportstijl", "abonnement"];
const longDate = (iso: string) =>
  new Date(iso).toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" });

/** Organisatie-instellingen (profielmenu → Organisatie): gebruikers, klanten, documentsoorten, rapportstijl en abonnement. */
export function OrganizationPage() {
  const { data: me } = useMe();
  const [params, setParams] = useSearchParams();
  const raw = params.get("tab") as Tab | null;
  const tab: Tab = raw && TABS.includes(raw) ? raw : "gebruikers";
  const manager = can(me, "users.manage") || me?.role === "owner";
  const sub = useQuery({
    queryKey: ["subscription", me?.org?.id],
    queryFn: () => api<OrgSubscriptionResponse>("/org/subscription"),
    enabled: manager,
  });
  if (!me) return null;
  if (!manager)
    return (
      <div className="center">
        <p>Alleen beheerders kunnen de organisatie-instellingen openen.</p>
      </div>
    );

  return (
    <>
      <PageHeader
        label="Organisatie"
        title={me.org?.name}
        tabs={
          <Tabs
            label="Onderdeel"
            items={[
              { value: "gebruikers", label: "Gebruikers" },
              { value: "klanten", label: "Klanten" },
              ...(hasModule(me, "dossier") ? [{ value: "documentsoorten" as const, label: "Documentsoorten" }] : []),
              { value: "rapportstijl", label: "Logo en huisstijl" },
              { value: "abonnement", label: "Abonnement" },
            ]}
            value={tab}
            onChange={(t) => setParams(t === "gebruikers" ? {} : { tab: t }, { replace: true })}
          />
        }
      />
      {tab === "gebruikers" && <UsersSection />}
      {tab === "klanten" && <ClientsSection canEdit={me.role === "owner" || me.role === "beheerder"} />}
      {tab === "documentsoorten" && <DocTypesSection canEdit={can(me, "document.types")} />}
      {tab === "rapportstijl" && (
        <section className="panel">
          <ReportStyleSection canEdit={me.role === "owner" || me.role === "beheerder"} />
        </section>
      )}
      {tab === "abonnement" && (
        <section className="panel" aria-label="Abonnement">
          {sub.isPending ? (
            <Loading />
          ) : sub.data ? (
            <dl className="subinfo">
              <dt>Abonnement</dt>
              <dd>
                {sub.data.readOnly && sub.data.status === "trial"
                  ? "Proefperiode afgelopen (alleen lezen)"
                  : ORG_STATUS_LABELS[sub.data.status]}
                {sub.data.status === "trial" &&
                  sub.data.trialEndsAt &&
                  !sub.data.readOnly &&
                  ` tot en met ${longDate(sub.data.trialEndsAt)}`}
              </dd>
              {sub.data.pricePerBuildingCents > 0 && (
                <>
                  <dt>Kosten</dt>
                  <dd>
                    {sub.data.buildings} {sub.data.buildings === 1 ? "gebouw" : "gebouwen"} ×{" "}
                    {formatEuro(sub.data.pricePerBuildingCents)} = {formatEuro(sub.data.monthlyCents)} per maand (excl.
                    btw)
                  </dd>
                </>
              )}
            </dl>
          ) : (
            <p className="muted">Het abonnement kon niet worden geladen.</p>
          )}
        </section>
      )}
    </>
  );
}
